import React, { useMemo } from 'react';
import Modal from '../../components/Modal';
import { useSchemaFieldDict } from '../../components/SchemaFieldKeySelect';
import { fieldDisplayName, toFieldNameMap } from '../../api/schemaField';
import { useDictOptions } from '../../utils/dictOptions';
import { gateFieldsOf } from '../../utils/fieldVisibility';
import { Layout, groupFields } from '../../utils/fieldGroups';
import { FieldSpec } from './SchemaEditModal';

// 参数模板只读预览 (2026-11-01): 点「预览」看模板有哪些字段 (显示名 / 类型 / 单位 / 默认值 / 范围 / 枚举 / 必填 / 联动),
// 不需要编辑权限; 有分组布局时按 页签 / 分组 分段.

const TYPE_LABEL: Record<string, string> = {
    num: '数值',
    int: '整数',
    str: '文本',
    bool: '是/否',
    enum: '枚举',
    array: '多值',
};

// 枚举候选最多列这么多个, 其余折成"等 N 个"
const ENUM_PREVIEW = 8;

function fmt(v: unknown): string {
    if (v === undefined || v === null || v === '') return '';
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
}

interface SchemaPreviewModalProps {
    title: string;
    subtitle?: string;
    fields: FieldSpec[];
    layout: Layout;
    /** scoped 字典类目的上下文 (archCode), 用于枚举 code → 名称 */
    dictScope?: string;
    onClose: () => void;
}

function SchemaPreviewModal({
    title,
    subtitle,
    fields,
    layout,
    dictScope,
    onClose,
}: SchemaPreviewModalProps) {
    const { dict } = useSchemaFieldDict();
    const names = useMemo(() => toFieldNameMap(dict), [dict]);
    const dictOpts = useDictOptions(
        fields.map((f) => f.os),
        dictScope,
    );
    const keys = useMemo(() => new Set(fields.map((f) => f.k)), [fields]);
    const tabs = useMemo(
        () => groupFields(fields, (f) => f, layout),
        [fields, layout],
    );

    const enumText = (f: FieldSpec): string => {
        const vals: string[] = f.os
            ? (dictOpts[f.os] || []).map((o) => o.label || o.code)
            : (f.o || []).map(fmt);
        if (!vals.length) return f.os ? `字典 ${f.os}` : '';
        const head = vals.slice(0, ENUM_PREVIEW).join(' / ');
        return vals.length > ENUM_PREVIEW
            ? `${head} 等 ${vals.length} 个`
            : head;
    };
    const rangeText = (f: FieldSpec): string =>
        f.min === undefined && f.max === undefined
            ? ''
            : `${fmt(f.min)} ~ ${fmt(f.max)}`;
    // 联动: 只列本模板里存在的门控字段 (门控字段不在模板里时规则不生效)
    const gateText = (k: string): string =>
        gateFieldsOf(k)
            .filter((g) => keys.has(g))
            .map((g) => fieldDisplayName(g, names))
            .join('、');

    let seq = 0;
    return (
        <Modal
            open
            title={title}
            subtitle={subtitle}
            onClose={onClose}
            width={900}
        >
            {fields.length === 0 ? (
                <div className="py-6 text-center text-xs text-slate-400">
                    该模板没有字段
                </div>
            ) : (
                <div className="space-y-4">
                    <div className="text-xs text-slate-500">
                        共 {fields.length} 个字段, 必填{' '}
                        {fields.filter((f) => f.required).length} 个
                    </div>
                    {tabs.map((t) =>
                        t.sections.map((s) =>
                            s.items.length === 0 ? null : (
                                <div key={`${t.tab}/${s.sec}`}>
                                    {(t.tab || s.sec) && (
                                        <div className="text-xs font-medium text-slate-600 mb-1">
                                            {[t.tab, s.sec]
                                                .filter(Boolean)
                                                .join(' / ')}
                                        </div>
                                    )}
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead>
                                                <tr className="text-left text-slate-400">
                                                    <th className="py-1.5 w-8">
                                                        #
                                                    </th>
                                                    <th>字段</th>
                                                    <th>类型</th>
                                                    <th>单位</th>
                                                    <th>默认值</th>
                                                    <th>范围</th>
                                                    <th>枚举</th>
                                                    <th>必填</th>
                                                    <th>联动</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {s.items.map((f) => {
                                                    seq += 1;
                                                    const gate = gateText(f.k);
                                                    return (
                                                        <tr
                                                            key={f.k}
                                                            className="border-t border-slate-100 align-top"
                                                        >
                                                            <td className="py-1.5 text-slate-400">
                                                                {seq}
                                                            </td>
                                                            <td className="py-1.5">
                                                                <div className="text-slate-700">
                                                                    {fieldDisplayName(
                                                                        f.k,
                                                                        names,
                                                                    )}
                                                                </div>
                                                                <div className="font-mono text-[10px] text-slate-400">
                                                                    {f.k}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                {TYPE_LABEL[
                                                                    f.t
                                                                ] || f.t}
                                                            </td>
                                                            <td>{f.u}</td>
                                                            <td>{fmt(f.v)}</td>
                                                            <td className="whitespace-nowrap">
                                                                {rangeText(f)}
                                                            </td>
                                                            <td className="max-w-[220px] text-slate-500">
                                                                {enumText(f)}
                                                            </td>
                                                            <td>
                                                                {f.required && (
                                                                    <span className="text-rose-500">
                                                                        必填
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="text-slate-500">
                                                                {gate &&
                                                                    `受 ${gate} 控制`}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ),
                        ),
                    )}
                </div>
            )}
        </Modal>
    );
}

export default SchemaPreviewModal;
